const DEFAULT_APP_PATH = '/mdc/auth/media';

const ALLOWED_ROUTE_PREFIXES = [
  '/mdc/auth/media',
  '/mdc/preview',
];

const APP_QUERY_FLAGS = {
  sms_config: '?sms_config=1',
  unzip: '?unzip=1',
  rename: '?rename=1',
  upload: '?upload=1',
  bulkupload: '?bulkupload=1',
  newfolder: '?newfolder=1',
  help: '?help=1',
};

const normalizePathSegments = (pathPart) => {
  const segments = pathPart.split('/').filter(Boolean);
  const normalized = [];

  for (const segment of segments) {
    if (segment === '..') {
      normalized.pop();
    } else if (segment !== '.') {
      normalized.push(segment);
    }
  }

  return `/${normalized.join('/')}`;
};

const isAllowedAppPath = (pathPart) => ALLOWED_ROUTE_PREFIXES.some(
  (prefix) => pathPart === prefix || pathPart.startsWith(`${prefix}/`)
);

/**
 * Validates and normalizes an in-app pathname to prevent open redirects.
 * Only same-origin dashboard and preview routes are allowed.
 */
export const sanitizeAppPathname = (pathname) => {
  const raw = typeof pathname === 'string' ? pathname : '';

  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) {
    return DEFAULT_APP_PATH;
  }

  if (/^\/[^/]*:/.test(raw) || /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(raw)) {
    return DEFAULT_APP_PATH;
  }

  const queryIndex = raw.indexOf('?');
  const pathPart = queryIndex === -1 ? raw : raw.slice(0, queryIndex);
  const normalizedPath = normalizePathSegments(pathPart);

  if (!isAllowedAppPath(normalizedPath)) {
    return DEFAULT_APP_PATH;
  }

  return normalizedPath;
};

/**
 * Validates a single path segment (file or folder name) for safe URL construction.
 */
export const sanitizePathSegment = (segment) => {
  if (typeof segment !== 'string' || !segment.length) {
    return null;
  }

  if (segment.includes('/') || segment.includes('\\') || segment === '..' || segment === '.') {
    return null;
  }

  return segment;
};

export const buildMediaChildPath = (pathname, segment) => {
  const safeBase = sanitizeAppPathname(pathname);
  const safeSegment = sanitizePathSegment(segment);

  return safeSegment ? `${safeBase}/${safeSegment}` : safeBase;
};

export const authMediaPathToPreview = (pathname) => {
  return sanitizeAppPathname(pathname).replace('/auth/media/', '/preview/');
};

export const toPreviewPath = (pathname, segment) => {
  const previewBase = authMediaPathToPreview(pathname);
  const safeSegment = sanitizePathSegment(segment);

  return safeSegment ? `${previewBase}/${safeSegment}` : previewBase;
};

export const appendAppQuery = (pathname, queryKey) => {
  const query = APP_QUERY_FLAGS[queryKey];

  if (!query) {
    return sanitizeAppPathname(pathname);
  }

  return `${sanitizeAppPathname(pathname)}${query}`;
};

export const buildMediaPathFromSuffix = (relativeSuffix) => {
  const raw = typeof relativeSuffix === 'string' ? relativeSuffix : '';
  const normalized = normalizePathSegments(raw.startsWith('/') ? raw : `/${raw}`);

  if (normalized === '/' || normalized === '/user') {
    return DEFAULT_APP_PATH;
  }

  return `${DEFAULT_APP_PATH}${normalized}`;
};

export const getMediaPathSuffix = (pathname) => {
  const safe = sanitizeAppPathname(pathname);

  if (safe.startsWith('/mdc/auth/media')) {
    return safe.slice('/mdc/auth/media'.length);
  }

  return '';
};

export const buildHelpPath = (pathname) => {
  const suffix = getMediaPathSuffix(pathname);
  return appendAppQuery(`${DEFAULT_APP_PATH}/v1${suffix}`, 'help');
};

export const buildHelpBackPath = (pathname) => {
  return sanitizeAppPathname(pathname).replace('/v1', '');
};

export const getDownloadFilePath = (pathname) => {
  const raw = typeof pathname === 'string' ? pathname : '';

  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) {
    return null;
  }

  if (/^\/[^/]*:/.test(raw) || /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(raw)) {
    return null;
  }

  const pathPart = raw.split('?')[0];
  const normalized = normalizePathSegments(pathPart);

  if (normalized !== '/mdc/download' && !normalized.startsWith('/mdc/download/')) {
    return null;
  }

  const filePath = normalized.slice('/mdc/download'.length);
  return filePath === '' ? '/' : filePath;
};

export const buildDownloadApiUrl = (pathname, apiBaseUrl) => {
  const base = typeof apiBaseUrl === 'string' ? apiBaseUrl.replace(/\/$/, '') : '';
  const filePath = getDownloadFilePath(pathname);

  if (!filePath || !base) {
    return '';
  }

  return `${base}/mdc/download${filePath}`;
};

export const getCurrentPathFromLocation = (pathname) => {
  const path = sanitizeAppPathname(pathname);
  const authPrefix = '/mdc/auth/';
  let uriPrefix = '';

  if (path.startsWith(authPrefix)) {
    const nextSlash = path.indexOf('/', authPrefix.length);
    uriPrefix = nextSlash === -1 ? path : path.slice(0, nextSlash);
  }

  let currentPath = path.replace(uriPrefix, '');
  const segments = currentPath.split('/').filter(Boolean);
  currentPath = `/${segments.join('/')}`;

  return currentPath === '' ? '/' : currentPath;
};
